import { Link } from 'react-router-dom';
import { PriorityIcon } from '@/components/PriorityIcon.js';
import { Avatar } from '@/components/Avatar.js';
import { IssueTypeIcon } from '@/components/IssueTypeIcon.js';
import { LinkIndicator } from '@/components/LinkIndicator.js';
import type { JiraIssueSummary } from '@shared/types.js';

interface IssueCardProps {
  issue: JiraIssueSummary;
}

/** Jira board card: summary on top; type, key and links bottom-left; priority and assignee bottom-right. */
export function IssueCard({ issue }: IssueCardProps) {
  const isDone = issue.status.category === 'done';
  return (
    <Link
      to={`/issues/${issue.key}`}
      className="block rounded-md border border-gray-200 bg-white p-3 shadow-[0_1px_1px_rgba(9,30,66,0.15)] transition-colors hover:bg-gray-50"
    >
      {issue.parent && (
        <p className="mb-1 truncate text-[11px] text-gray-500" title={`${issue.parent.key}: ${issue.parent.summary}`}>
          <span className="font-semibold">{issue.parent.key}</span> {issue.parent.summary}
        </p>
      )}
      <p className="text-sm leading-snug text-gray-900 line-clamp-3">{issue.summary}</p>
      {issue.reporter && <p className="mt-1 truncate text-[11px] text-gray-500" title={`Reporter: ${issue.reporter.displayName}`}>Requested by {issue.reporter.displayName}</p>}
      {issue.labels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {issue.labels.slice(0, 3).map((l) => (
            <span key={l} className="max-w-full truncate rounded-[3px] bg-gray-100 px-1.5 py-px text-[11px] text-gray-600">{l}</span>
          ))}
        </div>
      )}
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <IssueTypeIcon name={issue.issuetype.name} />
          <span className={`whitespace-nowrap text-xs font-semibold ${isDone ? 'text-gray-500 line-through' : 'text-gray-600'}`}>{issue.key}</span>
          <LinkIndicator links={issue.links} />
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <PriorityIcon name={issue.priority.name} iconUrl={issue.priority.iconUrl} />
          {issue.assignee ? (
            <Avatar name={issue.assignee.displayName} avatarUrl={issue.assignee.avatarUrl} size="sm" />
          ) : (
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-300" title="Unassigned" aria-label="Unassigned">
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true"><circle cx="8" cy="5.5" r="2.5" /><path d="M3 13c.6-2.5 2.6-4 5-4s4.4 1.5 5 4z" /></svg>
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
